import { NetworkOps } from '@activehub/common-components-spa';
import { catchError, map } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';

export const userLocale = navigator.language || 'en-US';

// Session ID stored in memory
let currentSessionId = null;

// All NetworkOps instances to attach session interceptor
const allApiInstances = [];

export const rootAPI = new NetworkOps({
    baseURL: '/api'
});

export const proxyAPI = new NetworkOps({
    baseURL: '/api/proxy/v1'
});

export const proxyAPIv2 = new NetworkOps({
    baseURL: '/api/proxy/v2'
});

export const gatewayConfigurationApi = new NetworkOps({
    baseURL: '/api/configuration/v1',
});

export const permissionAPI = new NetworkOps({
    baseURL: '/api/permission/v1'
});

export const userAbilityAPI = new NetworkOps({
    baseURL: '/api'
});

export const accessCodeApi = new NetworkOps({
    baseURL: "/api/entitlementaccesscodes/v1"
})

export const userPreferenceAPI = new NetworkOps({
    baseURL: '/api/preference/v1'
});

export const organizationAPI = new NetworkOps({
    baseURL: '/api/organization/v1'
});

export const singleUserAddServicesApi = new NetworkOps({
  baseURL: '/api/bulkuserprocess/v2'
});
/* whenever a new networkOps is defined add it in setAuthToken and setUserIdHeader */

export function setAuthToken(token) {
    rootAPI.setAuthToken(token);
    proxyAPI.setAuthToken(token);
    proxyAPIv2.setAuthToken(token);
    gatewayConfigurationApi.setAuthToken(token);
    permissionAPI.setAuthToken(token);
    userAbilityAPI.setAuthToken(token);
    accessCodeApi.setAuthToken(token);
    userPreferenceAPI.setAuthToken(token);
    organizationAPI.setAuthToken(token);
    singleUserAddServicesApi.setAuthToken(token);
}

export function setUserIdHeader(id) {
    rootAPI.setUserId(id);
    proxyAPI.setUserId(id);
    proxyAPIv2.setUserId(id);
    gatewayConfigurationApi.setUserId(id);
    permissionAPI.setUserId(id);
    userAbilityAPI.setUserId(id);
    accessCodeApi.setUserId(id);
    userPreferenceAPI.setUserId(id);
    organizationAPI.setUserId(id);
    singleUserAddServicesApi.setUserId(id);
}

export function setSessionIdHeader(sessionId) {
    currentSessionId = sessionId;
    rootAPI.setSessionId(sessionId);
    proxyAPI.setSessionId(sessionId);
    proxyAPIv2.setSessionId(sessionId);
    gatewayConfigurationApi.setSessionId(sessionId);
    permissionAPI.setSessionId(sessionId);
    userAbilityAPI.setSessionId(sessionId);
    accessCodeApi.setSessionId(sessionId);
    userPreferenceAPI.setSessionId(sessionId);
    organizationAPI.setSessionId(sessionId);
    singleUserAddServicesApi.setSessionId(sessionId);
    // Share with micro-frontends
    window.GATEWAY = Object.assign(window.GATEWAY || {}, { sessionId });
}

export function setPmrIdHeader(pmrId) {
  rootAPI.setPmrId(pmrId);
  organizationAPI.setPmrId(pmrId);
}

export function getUserProfile(userId) {
    return userAbilityAPI.get(`/user/v1/user/${userId}`);
}

/**
 * Update the user profile
 * @param userId 
 * @param data 
 */
export function updateUserProfile(userId, data) {
    return userAbilityAPI
        .patch(`/user/v1/user/${userId}/profile`, data);
}

/**
 * Update the user login date
 * @param userId 
 * @param orgId
 * @param data 
 */
export function updateUserLastLogin(userId, orgId, data) {
    return organizationAPI
        .patch(`/organization/${orgId}/user/${userId}/lastLoginDate`, data);
}

/**
 * Getting the Role of the user
 * @param data 
 * @param token 
 */
export function getUserRole(data) {
    return userAbilityAPI
        .post('/usersearch/v2/search/users', data);
}


// Store the full organization response for later use
let cachedOrgResponse = null;

export function getHeaderInfo(userId) {
    // return gatewayConfigurationApi.get(`/configuration/findByHostname/${window.location.host}/locale/${userLocale}`);
    return proxyAPIv2.get(`/organisationDetailsForUser/user/${userId}`).pipe(
        map(response => {
            // Cache the full response for organization data extraction
            cachedOrgResponse = response;
            
            // Transform new API response to match old format
            const orgData = response?.organisationDetails?.data?.[0];
            if (!orgData) {
                throw new Error('No organization data found');
            }

            // Extract region information
            const regionId = orgData.productModelRegions?.[0]?.regionId || orgData.regonDetails?.[0]?.id;
            const regionName = orgData.productModelRegions?.[0]?.regionName || orgData.regonDetails?.[0]?.name;

            // Map to old API response format
            return {
                regionId: regionId || '',
                name: regionName || orgData.name || '',
                description: orgData.description || '',
                welcome: '', // Not available in new API
                headerImageUrl: `${process.env.PUBLIC_URL || ''}/images/Pearson-logo-desktop.svg`, // Default logo
                styleSheetUrl: '', // Not available in new API  
                landingPageId: '', // Not available in new API
                copyright: 'Copyright © 2026 Pearson All rights reserved', // Default copyright
                audit: orgData.audit || {
                    dateCreated: new Date().toISOString(),
                    dateModified: new Date().toISOString(),
                    createdBy: '',
                    modifiedBy: ''
                },
                status: orgData.status || 'ACTIVE'
            };
        })
    );
}

export function getSpas(region, locale = userLocale) {
    return gatewayConfigurationApi.get(
        `/configuration/region/${region}/locales/${locale}/spas`);
}

export function getSPAsInRegion(region, locale = userLocale) {
    return proxyAPI.get(`/getSPAInRegion/${region}/locale/${locale}`)
}

export function getListofTranslation() {
    return userAbilityAPI.get(`/getTranslationList`);
}

export function getUserAbilities(userId, regionId) {
    return proxyAPI.get(`/getUserAbilityByUserIdRegionId/${userId}/${regionId}`)
}
export function getUserAbilitiesWithLevel(userId, regionId) {
    return proxyAPIv2.get(`/getUserAbilityByUserIdRegionId/${userId}/${regionId}`)
}

export function getAppConfig() {
    return rootAPI.get(`/getAppConfig`)
}

export function redeemAcessCode(data,accessCodeValue,accessCodeOrigin){
    return accessCodeApi.post(`/accesscode/byvalue/${accessCodeValue}/${accessCodeOrigin}/redeem`,data)
}

export function getLiveChatConfig() {
    return rootAPI.get(`/getLiveChatConfig`)
}

export function getAppcuesConfig() {
    return rootAPI.get(`/getAppcuesConfig`)
}

// export function sessionLoginApi(payload, token, userId) {
//     return new Observable(subscriber => {
//         fetch('/api/proxy/v1/session/login', {
//             method: 'POST',
//             headers: {
//                 'Content-Type': 'application/json',
//                 'Authorization': token,
//                 'x-prsn-user-id': userId
//             },
//             credentials: 'include',
//             body: JSON.stringify(payload)
//         })
//         .then(res => res.json())
//         .then(data => {
//             if (data.sessionId) {
//                 setSessionIdHeader(data.sessionId);
//             }
//             subscriber.next(data);
//             subscriber.complete();
//         })
//         .catch(err => {
//             subscriber.error(err);
//         });
//     });
// }

export function sessionLoginApi(userId) {
    return proxyAPI.post('/session/login', {userId});
}

export function sessionLogoutApi(sessionId) {
    return proxyAPI.post('/session/logout', { sessionId });
}

export function getChatToken(body) {
    return proxyAPI.post('/chatKey', body);
}

export function getUserStatus() {
    return proxyAPI.get('/getUserStatus')
}

export function getOrganisationDetailsbyCountry(organizationId) {
    return proxyAPI.get(`/organisation-details/${organizationId}`)
}

export function getOrganisationByUserId(userId) {
    // Use cached organization data from header API response instead of separate API call
    //return organizationAPI.get(`/organizations/inProductModelRegion/byUser/${userId}`)
    return of(cachedOrgResponse?.organisationDetails?.data || []).pipe(
        map(orgData => {
            // Transform the new API response to match the old format expected by the app
            return orgData && orgData.map(org => ({
                organizationId: org.organizationId,
                organizationGroupId: org.organizationGroupIds?.[0] || org.organizationGroupId || null,
                productModelRegions: org.productModelRegions?.map(pmr => pmr.id) || [],
                orgGroupPmrId: org.productModelRegions?.[0]?.id || null,
                name: org.name,
                description: org.description,
                status: org.status,
                userStatus: org.userStatus
            }));
        })
    );
}

export function fetchRoleInOrg(orgId, pmrId, userId) {
    return organizationAPI.get(`/organization/${orgId}/productModelRegions/${pmrId}/user/${userId}/permissionGroups`)
  }

export function fetchRoleInOrgv2(userId, orgId = null, orgGroupId = null, orgPmrs = [], orgGroupPmr = null) {
    return proxyAPI.post(`/userability/${userId}`, {
        organizationGroupId: orgGroupId,
        orgGroupPmrId: orgGroupPmr,
        organizationId: orgId,
        productModelRegions: orgPmrs
    })
  } 

export function getAccessCodeValidate(accessCode) {
    return rootAPI.get(`/entitlementaccesscodes/v2/accesscode/${accessCode}`)
}

export function getAccessCodeData(data) {
    return proxyAPI.get(`/validateAccessCode/${data}`);
}

export function activateAccessCode(accessCode, userId, orgId) {
    return rootAPI.post(`/prdmgmtsubscription/v3/subscription/user/${userId}/accesscode/${accessCode}`, {
        userId: [userId],
        organizationId: orgId,
        lob: "AH"
    })
} 

export function addNewUserToD2C(postData, prmId, d2cOrg) {
  return singleUserAddServicesApi.post(`/bulkuser/createandregister/${d2cOrg}/${prmId}`, postData)
}

export function accessCodeRedemption(accessCode, payload) {
    return proxyAPI.post(`/redeemAccessCode/${accessCode}`, payload);
}

export default {
    getHeaderInfo,
    getSpas,
    setAuthToken,
    getUserProfile,
    setUserIdHeader,
    setPmrIdHeader,
    getListofTranslation,
    redeemAcessCode,
    getOrganisationByUserId,
    getAccessCodeValidate,
    activateAccessCode,
    addNewUserToD2C,
    accessCodeRedemption,
    sessionLoginApi,
    sessionLogoutApi,
    getChatToken,
    getOrganisationDetailsbyCountry,
};
